import { Directive, Input, TemplateRef, ViewContainerRef, ViewRef, inject } from "@angular/core";
import { TreeNode } from "@tylertech/tyler-api-types";

export function isLeaf(node: TreeNode): boolean
{
    return !node.Children?.length;
}

export interface TreeContext<T = TreeNode>
{
    $implicit: T;
    index?: number;
}

@Directive({ selector: "[isLeaf]" })
export class IsLeafDirective
{
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly templateRef = inject<TemplateRef<TreeContext>>(TemplateRef);

    @Input()
    set isLeaf(value: TreeNode)
    {
        this._context.$implicit = value;
        this.condition = value;
    }

    /**
     * A template to show if the condition expression evaluates to false.
     */
    @Input()
    set isLeafElse(templateRef: TemplateRef<TreeContext | undefined>)
    {
        this.elseTemplateRef = templateRef;
    }

    @Input() set isLeafIndex(value: number | undefined)
    {
        this._context.index = value;
    }

    private _context: TreeContext;
    private _thenViewRef: ViewRef;
    private _elseTemplateRef: TemplateRef<TreeContext>;
    private _elseViewRef: ViewRef;
    private conditionTrue: boolean;

    constructor()
    {
        this._context = {
            $implicit: undefined as TreeNode,
        };
    }

    protected set condition(value: TreeNode)
    {
        this.conditionTrue = isLeaf(value);

        this.updateView();
    }

    protected set elseTemplateRef(elseTemplateRef: TemplateRef<TreeContext>)
    {
        this._elseTemplateRef = elseTemplateRef;
        this._elseViewRef = undefined;

        this.updateView();
    }

    private updateView(): void
    {
        if (this.conditionTrue)
        {
            if (!this._thenViewRef)
            {
                this.viewContainerRef.clear();
                this._elseViewRef = undefined;
                if (this.templateRef)
                {
                    this._thenViewRef = this.viewContainerRef.createEmbeddedView(this.templateRef, this._context);
                }
            }
        }
        else
        {
            if (!this._elseViewRef)
            {
                this.viewContainerRef.clear();
                this._thenViewRef = undefined;
                if (this._elseTemplateRef)
                {
                    this._elseViewRef = this.viewContainerRef.createEmbeddedView(this._elseTemplateRef, this._context);
                }
            }
        }
    }

    // The "ngTemplateGuard_" prefix is a Angular naming convention
    // eslint-disable-next-line @typescript-eslint/naming-convention
    static ngTemplateGuard_isLeaf(
        _dir: IsLeafDirective, state: TreeNode): state is TreeNode
    {
        return true;
    }

    /**
     * Asserts the correct type of the context for the template that `NgIf` will render.
     *
     * The presence of this method is a signal to the Ivy template type-check compiler that the
     * `NgIf` structural directive renders its template with a specific context type.
     */
    static ngTemplateContextGuard(dir: IsLeafDirective, ctx: unknown): ctx is TreeContext
    {
        return true;
    }
}
